import { type HomeyAPI } from 'homey-api';
import { shallow } from 'zustand/shallow';

import { BaseApiStore, FetchArgs } from '../../../store/BaseApiStore.ts';

import { wait } from '../../../lib/wait';

import { useAttach } from '../../../store/useAttach';
import { selectBase } from '../../../store/selectors.ts';

export interface State {
  optionLanguageData: object | null;
  isLoading: boolean;
  error: unknown;
}

export type LanguageSettingsStoreState = State;

class LanguageSettingsStore extends BaseApiStore<State> {
  constructor() {
    super('language-settings');
  }

  override createInitialState() {
    return {
      optionLanguageData: null,
      isLoading: true,
      error: null,
    };
  }

  override async fetch({ api, silent, cache, abortController }: FetchArgs) {
    if (silent === false) {
      this.set(this.createInitialState());
    }

    try {
      const time = silent === true ? 0 : 0;
      const waitPromise = wait(time);

      const optionLanguagePromise = api.i18n.getOptionLanguage();

      const [, optionLanguageData] = await Promise.all([waitPromise, optionLanguagePromise]);

      if (abortController.signal.aborted) {
        return;
      }

      this.set({
        ...this.createInitialState(),
        optionLanguageData: optionLanguageData as unknown as object,
        isLoading: false,
      });
    } catch (error) {
      if (abortController.signal.aborted) return;

      this.set({
        ...this.createInitialState(),
        isLoading: false,
        error,
      });
    }
  }

  override destroy() {}

  override onAttached({ api }: { api: HomeyAPI }): void {}

  override onDetached({ api }: { api: HomeyAPI }): void {}

  use() {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useAttach(this, this.key);

    return { languageSettings: this.store(selectAll, shallow) };
  }

  async setOptionLanguage({ value }: { value: string }) {
    const api = this.getApi();
    const result = (await api.i18n.setOptionLanguage({ value })) as unknown as Promise<{
      value: string;
    }>;
    this.set({ optionLanguageData: result });
  }
}

function selectAll(state: LanguageSettingsStoreState) {
  return {
    ...selectBase(state),
    optionLanguageData: state.optionLanguageData,
  };
}

export const languageSettingsStore = new LanguageSettingsStore();
